import { useAsObservableSource, useLocalStore } from 'mobx-react-lite';

import {
  IcAddUserIcon,
  IcBankAccountIcon,
  IcBlockIcon,
  IcChangeOrderIcon,
  IcClearIcon,
  IcCloseIcon,
  IcDeleteIcon,
  IcEditIcon,
  IcLogOutIcon,
  IcPinStrokeIcon,
  IcPrivacyIcon,
  IcReportIcon,
  IcUsersIcon,
  IcAppearanceIcon,
} from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';

import Tasks from 'APP/Tasks';
import { MAX_PIN_GROUPS } from 'APP/constants/group';
import { AddParticipantsPopup } from 'APP/main/PopupManager/Popups/AddParticipantsPopup/AddParticipantsPopup';
import { ChatBackgroundPopup } from 'APP/main/PopupManager/Popups/ChatBackgroundPopup/ChatBackgroundPopup';
import { EditChannelPopup } from 'APP/main/PopupManager/Popups/EditChannelPopup/EditChannelPopup';
import { GroupBlackListPopup } from 'APP/main/PopupManager/Popups/GroupBlackListPopup/GroupBlackListPopup';
import { GroupPermissionsPopup } from 'APP/main/PopupManager/Popups/GroupPermissionsPopup/GroupPermissionsPopup';
import { MonetizationPopup } from 'APP/main/PopupManager/Popups/MonetizationPopup/MonetizationPopup';
import { ParticipantsPopup } from 'APP/main/PopupManager/Popups/ParticipantsPopup/ParticipantsPopup';
import { ReportGroupPopup } from 'APP/main/PopupManager/Popups/ReportGroupPopup/ReportGroupPopup';
import { useMoveGroupToSpace } from 'APP/main/hooks/space/useMoveGroupToSpace';
import { GroupType } from 'APP/model/group/groupModel.types';
import { Features } from 'APP/packages/features/Features';
import Entities from 'STORE';

import useSharedDataItems from '../hooks/sharedDataItems';

export const useChannelProfilePresenter = ({ groupId, actions }) => {
  const { t } = useTranslation();
  const sharedDataItems = useSharedDataItems({ groupId, actions });
  const source = useAsObservableSource({ groupId });
  const moveGroupToSpace = useMoveGroupToSpace(groupId);

  const presenter = useLocalStore(() => ({
    get group() {
      return Entities.GroupsStore.getGroupById(source.groupId);
    },

    get space() {
      const spaceId = presenter.group.spaceId;

      return spaceId ? Entities.spacesStore.getById(spaceId) : null;
    },

    get title() {
      return presenter.group.avatarTitle;
    },

    get subtitle() {
      return t('amount_of_subscribers_in_a_channel_many', { 0: presenter.group.usersCount });
    },

    get description() {
      return presenter.group.description;
    },

    get canInviteUsers() {
      if (presenter.group.isOpen) {
        return true;
      }

      return presenter.group.canInviteUsers;
    },

    get childPagesList() {
      return [
        {
          key: 'subscribers',
          Icon: IcUsersIcon,
          title: t('common_subscribers'),
          onClick: presenter.openSubscribers,
          count: presenter.group.usersCount,
          isVisible: presenter.isVisibleSubscribers,
        },
        ...sharedDataItems.items,
      ].filter((item) => item.isVisible);
    },

    get isVisibleSubscribers() {
      return presenter.group.withMeInAdmins;
    },

    openSubscribers() {
      Tasks.app.openPopup(ParticipantsPopup, { params: { groupId: source.groupId } });
    },

    get actions() {
      const items = [
        {
          key: 'blacklist',
          isVisible: presenter.isVisibleBlacklist,
          Icon: IcBlockIcon,
          title: t('balck_list_title'),
          onClick: presenter.showBlacklist,
        },
        {
          key: 'monetization',
          isVisible: presenter.isVisibleMonetization,
          Icon: IcBankAccountIcon,
          title: t('common_monetization'),
          onClick: presenter.openMonetization,
        },
        {
          key: 'permissions',
          isVisible: presenter.isVisiblePermissions,
          Icon: IcPrivacyIcon,
          title: t('permissions_label'),
          onClick: presenter.showPermissions,
        },
      ];

      return items.filter((item) => item.isVisible);
    },

    get isVisibleBlacklist() {
      if (!Features.isBanUserEnabled()) {
        return false;
      }
      return presenter.group.withMeInAdmins;
    },

    showBlacklist() {
      presenter.childPopup = Tasks.app.openPopup(GroupBlackListPopup, {
        params: { groupId: presenter.group.id },
      });
    },

    get isVisiblePermissions() {
      if (!Features.isPermissionsGroupEnabled()) {
        return false;
      }

      return presenter.group.withMeInAdmins;
    },

    showPermissions() {
      presenter.childPopup = Tasks.app.openPopup(GroupPermissionsPopup, {
        params: { groupId: presenter.group.id },
      });
    },

    get isVisibleMonetization() {
      return (
        Features.isMonetizationEnabled() &&
        presenter.group.isOwner &&
        presenter.group.type === GroupType.ChannelOpen
      );
    },

    openMonetization() {
      Tasks.app.openPopup(MonetizationPopup, { params: { groupId: source.groupId } });
    },

    get actionItems() {
      const items = [
        {
          key: 'add-people',
          isVisible: presenter.canAddPeople,
          Icon: IcAddUserIcon,
          title: t('group_chat_add_people_button'),
          onClick: presenter.addPeople,
        },
        {
          key: 'pin-channel',
          isVisible: presenter.canPinChannel,
          Icon: IcPinStrokeIcon,
          title: t('channel_pin'),
          onClick: presenter.pinChannel,
        },
        {
          key: 'unpin-channel',
          isVisible: presenter.canUnpinChannel,
          Icon: IcPinStrokeIcon,
          title: t('channel_unpin'),
          onClick: presenter.unpinChannel,
        },
        {
          key: 'edit-channel',
          isVisible: presenter.canEditChannel,
          Icon: IcEditIcon,
          title: t('channel_edit'),
          onClick: presenter.editChannel,
        },
        {
          key: 'chat-appearance',
          isVisible: true,
          Icon: IcAppearanceIcon,
          title: t('chat_appearance'),
          onClick: presenter.openAppearance,
        },
        {
          key: 'move-to-space',
          isVisible: presenter.canMoveToSpace,
          Icon: IcChangeOrderIcon,
          title: t('space_moveto_space_button'),
          onClick: presenter.moveToSpace,
        },
        {
          key: 'remove-from-space',
          isVisible: presenter.canRemoveFromSpace,
          Icon: IcCloseIcon,
          title: t('space_remove_from_button', { 0: presenter.space?.name }),
          onClick: presenter.removeFromSpace,
        },
        {
          key: 'report-channel',
          isVisible: presenter.canReportChannel,
          Icon: IcReportIcon,
          title: t('common_report'),
          onClick: presenter.reportChannel,
        },
        {
          key: 'clear-messages',
          isVisible: presenter.canClearHistory,
          Icon: IcClearIcon,
          title: t('profile_clear_messages'),
          onClick: presenter.clearHistory,
        },
        {
          key: 'delete-channel',
          isVisible: presenter.canDeleteChannel,
          Icon: IcDeleteIcon,
          title: t('channel_delete'),
          onClick: presenter.deleteChannel,
        },
        {
          key: 'leave-channel',
          isVisible: presenter.canLeaveChannel,
          Icon: IcLogOutIcon,
          title: t('channel_leave'),
          onClick: presenter.leaveChannel,
        },
      ];

      return items.filter((item) => item.isVisible);
    },

    get canAddPeople() {
      if (!presenter.group) {
        return false;
      }

      return (
        (presenter.group.isChannel && presenter.group.withMeInAdmins) ||
        presenter.group.canInviteUsers
      );
    },

    addPeople() {
      Tasks.app.openPopup(AddParticipantsPopup, { params: { groupId: source.groupId } });
    },

    get canPinMore() {
      return Entities.GroupsStore.pinnedChannels.length < MAX_PIN_GROUPS;
    },

    get canPinChannel() {
      if (!presenter.group || !presenter.group.isChannel || presenter.group.withoutMe) {
        return false;
      }

      return presenter.canPinMore && !presenter.group.isPinned;
    },

    pinChannel() {
      if (!presenter.group) {
        return;
      }

      Tasks.group.pinGroup({ groupId: presenter.group.id });
    },

    get canUnpinChannel() {
      if (!presenter.group || !presenter.group.isChannel || presenter.group.withoutMe) {
        return false;
      }

      return presenter.group.isPinned;
    },

    unpinChannel() {
      if (!presenter.group) {
        return;
      }

      Tasks.group.unpinGroup({ groupId: presenter.group.id });
    },

    get canEditChannel() {
      if (!presenter.group) {
        return false;
      }

      return presenter.group.isChannel && presenter.group.withMeInAdmins;
    },

    editChannel() {
      Tasks.app.openPopup(EditChannelPopup, { params: { groupId: source.groupId } });
    },

    openAppearance() {
      if (!presenter.group) {
        return;
      }

      Tasks.app.openPopup(ChatBackgroundPopup);
    },

    get canMoveToSpace() {
      return moveGroupToSpace.canMoveToSpace;
    },

    moveToSpace() {
      presenter.childPopup = moveGroupToSpace.moveToSpace();
    },

    get canRemoveFromSpace() {
      return moveGroupToSpace.canRemoveFromSpace;
    },

    removeFromSpace() {
      moveGroupToSpace.removeFromSpace();
    },

    get canReportChannel() {
      if (!Features.isReportsEnabled()) {
        return false;
      }

      if (!presenter.group) {
        return false;
      }

      return !presenter.group.isOwner && presenter.group.isChannel;
    },

    reportChannel() {
      Tasks.app.openPopup(ReportGroupPopup, { params: { groupId: source.groupId } });
    },

    get canClearHistory() {
      if (!presenter.group) {
        return false;
      }

      return presenter.group.isOwner;
    },

    async clearHistory() {
      if (!presenter.group) {
        return;
      }

      const isConfirmed = await Tasks.app.showConfirm({
        text: t('channel_clear_history_alert'),
      });

      if (isConfirmed) {
        Tasks.group.clearHistory({
          groupId: presenter.group.id,
          isClearForAll: presenter.group.isChannel && presenter.group.isOwner,
        });
      }
    },

    get canDeleteChannel() {
      if (!presenter.group) {
        return false;
      }

      return presenter.group.isChannel && presenter.group.isOwner;
    },

    deleteChannel() {
      Tasks.app.showConfirm({
        text: t('delete_channel_alert'),
        async onSuccess() {
          await Tasks.group.deleteGroup({
            groupId: presenter.group.id,
          });
        },
      });
    },

    get canLeaveChannel() {
      if (!presenter.group || presenter.group.withoutMe) {
        return false;
      }

      return (
        !presenter.group.isOwner && presenter.group.isChannel && presenter.group.usersCount > 1
      );
    },

    leaveChannel() {
      if (!presenter.group) {
        return;
      }

      Tasks.app.showConfirm({
        text: t('leave_channel_alert'),
        async onSuccess() {
          await Tasks.group.leaveGroup({
            groupId: presenter.group.id,
          });
        },
      });
    },
  }));

  return presenter;
};
